import { useThree } from '@react-three/fiber';
import { EffectComposer, N8AO, SMAA as Smaa } from '@react-three/postprocessing';
import { useMemo } from 'react';
import { useShallow } from 'zustand/shallow';

import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

/**
 * Post-processing effects (AO + SMAA) driven by graphicsSettings.aoQuality.
 * Drop inside any R3F Canvas that should share consistent post-processing behaviour.
 */
export const ScenePostProcessing = () => {
    const { gl, size } = useThree((state) => ({ gl: state.gl, size: state.size }));

    const { aoQuality, multisampling } = usePlacementStore(
        useShallow((state) => ({
            aoQuality: state.graphicsSettings.aoQuality,
            multisampling: state.graphicsSettings.multisampling
        }))
    );

    const aoIntensity = useMemo(() => (aoQuality === 'off' ? 0 : 2), [aoQuality]);
    const aoActiveQuality = useMemo(
        () => (aoQuality === 'off' ? 'performance' : (aoQuality as 'performance' | 'high' | 'ultra')),
        [aoQuality]
    );

    const isReady = useMemo(
        () => gl && gl.getContext().getContextAttributes?.() && size.width > 0 && size.height > 0,
        [gl, size.height, size.width]
    );
    if (!isReady) {
        return null;
    }

    return (
        <EffectComposer multisampling={multisampling}>
            {aoQuality !== 'off' ? (
                <>
                    <N8AO aoRadius={0.8} distanceFalloff={0.5} intensity={aoIntensity} quality={aoActiveQuality} />
                    <Smaa />
                </>
            ) : (
                <></>
            )}
        </EffectComposer>
    );
};
